import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Toolbar,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import TextInput from '../components/Form/TextInput';
import { Logo } from '../components/logo';
import { useSettings } from '../contexts/Settings/settings-context';
import { ProductFeatures } from '../components/auth/product-features';
import { Auth } from '../api/Endpoints/Auth';
import { useAuth } from '../hooks/use-auth';

const schema = yup.object().shape({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup.string().required('Password is required'),
});

export const Login = () => {
  const navigate = useNavigate();
  const settings = useSettings();
  const { setUserData } = useAuth();
  const [loading, setLoading] = useState(false);

  const { handleSubmit, watch, control } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await Auth.login({ email: data.email, password: data.password });
      const result = response.data?.data || response.data;

      if (result.requires2FA) {
        if (result.setupRequired && result.qrCode) {
          // First-time 2FA setup
          navigate('/auth/2fa-setup', {
            state: { qrCode: result.qrCode, email: result.email },
          });
        } else {
          // Existing 2FA — just verify
          navigate('/auth/2fa-verify', {
            state: { email: result.email },
          });
        }
      } else if (result.token) {
        // Trusted IP — 2FA skipped, token returned directly
        const success = await setUserData({
          token: result.token,
          id: result.userId,
          name: result.name,
          email: result.email,
          role: result.role,
        });
        if (success) {
          navigate('/dashboard', { replace: true });
        } else {
          toast.error('Authentication failed');
        }
      }
    } catch (error) {
      const message = error?.message || error?.data?.message || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign In | Soul of Work - Accounts</title>
      </Helmet>
      <AppBar elevation={0} sx={{ backgroundColor: 'background.paper' }}>
        <Container maxWidth="md">
          <Toolbar disableGutters sx={{ height: 64 }}>
            <RouterLink to="/">
              <Logo variant={settings.theme === 'dark' ? 'light' : 'dark'} />
            </RouterLink>
          </Toolbar>
        </Container>
      </AppBar>

      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          pt: '64px',
        }}
      >
        <Box sx={{ py: 9 }}>
          <Container maxWidth="md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="hidden md:block">
                <ProductFeatures />
              </div>
              <div className="w-full">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Card sx={{ backgroundColor: 'background.default' }} elevation={0}>
                    <CardContent>
                      <Box sx={{ alignItems: 'center', display: 'flex', mb: 3 }}>
                        <Typography color="textPrimary" variant="h4">
                          Sign In
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                      </Box>
                      <div className="flex flex-col gap-4">
                        <div className="w-full">
                          <TextInput
                            control={control}
                            name="email"
                            label="Email address"
                            value={watch('email')}
                          />
                        </div>
                        <div className="w-full">
                          <TextInput
                            control={control}
                            name="password"
                            label="Password"
                            value={watch('password')}
                            type="password"
                          />
                        </div>
                        <LoadingButton
                          fullWidth
                          color="primary"
                          loading={loading}
                          variant="contained"
                          type="submit"
                        >
                          Sign In
                        </LoadingButton>
                      </div>
                      <Button
                        color="primary"
                        onClick={() => navigate('/forgot-password')}
                        variant="text"
                        sx={{ mt: 2 }}
                      >
                        Forgot Password?
                      </Button>
                    </CardContent>
                  </Card>
                </form>
              </div>
            </div>
          </Container>
        </Box>
      </Box>
    </>
  );
};
